import { defFor } from '../cards/cardHelpers';
import { squareRequiredAtPlayerCount } from '../constants';
import { applyResourceReward } from './rewards';

// How many charges a station/deployed ability slot NEEDS to fill before it
// can be activated, at the current player count (rulebook p21's pip-scaling
// worked example - see squareRequiredAtPlayerCount's own comment).
export function chargesRequired(ability, playerCount) {
  if (!ability) return 0;
  if (!ability.linked) return 1; // any one unlinked square unlocks it
  return ability.pips.filter((pip) => squareRequiredAtPlayerCount(pip, playerCount)).length;
}

export function isAbilityCharged(instance, cardSlotKey, ability, playerCount) {
  const filled = cardSlotKey === 'station' ? instance.stationCharges : instance.deployedCharges;
  return filled >= chargesRequired(ability, playerCount);
}

// Applies one charge to a card's station/deployed ability, if it has room
// (never over-fills past the number of physical squares printed).
export function addCharge(instance, slotKey, maxSquares) {
  const key = slotKey === 'station' ? 'stationCharges' : 'deployedCharges';
  if (instance[key] >= maxSquares) return instance;
  return { ...instance, [key]: instance[key] + 1 };
}

function findInstance(player, instanceId) {
  const sectorEntry = Object.entries(player.sectors).find(([, s]) => (
    s.station?.instanceId === instanceId || s.deployedStack.some((d) => d.instanceId === instanceId)
  ));
  if (!sectorEntry) return null;
  const [sectorNum, s] = sectorEntry;
  if (s.station?.instanceId === instanceId) return { sectorNum: Number(sectorNum), where: 'station', instance: s.station };
  const instance = s.deployedStack.find((d) => d.instanceId === instanceId);
  return { sectorNum: Number(sectorNum), where: 'deployed', instance };
}

function updateInstance(player, instanceId, updater) {
  const found = findInstance(player, instanceId);
  if (!found) return player;
  const { sectorNum, where } = found;
  const sector = player.sectors[sectorNum];
  if (where === 'station') {
    return { ...player, sectors: { ...player.sectors, [sectorNum]: { ...sector, station: updater(sector.station) } } };
  }
  return {
    ...player,
    sectors: {
      ...player.sectors,
      [sectorNum]: {
        ...sector,
        deployedStack: sector.deployedStack.map((d) => (d.instanceId === instanceId ? updater(d) : d)),
      },
    },
  };
}

// Spends charges for the given slot's ability - normally resets the
// slot's charge count straight to 0 (rulebook p20: "once used, back to
// needing a full charge again"). Shy Pluto's own "Trumbull Class Research
// Station" ships (Reiter etc. - see turnActions.js's own `repeatable`
// comment) are a genuine exception Matt described directly: up to 4
// charges can be BANKED (2 separate pairs), and spending only consumes
// the 2 actually required for that activation, leaving any banked
// remainder intact rather than wiping it - `amountToSpend`, when passed,
// subtracts instead of zeroing.
export function spendCharges(player, instanceId, slotKey, amountToSpend = null) {
  const key = slotKey === 'station' ? 'stationCharges' : 'deployedCharges';
  return updateInstance(player, instanceId, (inst) => (
    { ...inst, [key]: amountToSpend == null ? 0 : Math.max(0, inst[key] - amountToSpend) }
  ));
}

// Applies an ATOMIC ability/text-reward effect (one that resolves fully
// in-place, no further player input needed beyond `params`). Effects that
// instead grant a later capability (an extra buy, a claim) are handled by
// the caller (turnActions.js) via meta.pendingExtraBuys/pendingClaims,
// not here.
export function applyAtomicEffect(state, playerId, effect, params = {}) {
  const playerIndex = state.players.findIndex((p) => p.id === playerId);
  const player = state.players[playerIndex];

  switch (effect.kind) {
    case 'swapSectors': {
      const a = player.sectors[effect.sectorA];
      const b = player.sectors[effect.sectorB];
      const nextPlayer = {
        ...player,
        sectors: { ...player.sectors, [effect.sectorA]: b, [effect.sectorB]: a },
      };
      return replacePlayer(state, playerIndex, nextPlayer);
    }
    case 'exchangeCard': {
      // Matt reported this directly (full-audit pass): this read
      // `params.instanceId`, but useAbility (turnActions.js) only ever
      // passes the activated card's own instance id as
      // `params.sourceInstanceId` (the naming every OTHER effect here
      // that needs "which card triggered this" already uses - see
      // placeChargeAll's own sourceInstanceId exclusion just below). That
      // mismatch meant `findInstance` always got `undefined` and this
      // effect could NEVER fire, for any card, regardless of whether a
      // target picker existed - not just a missing-UI gap, the mechanic
      // itself was dead on arrival.
      const { targetSector } = params;
      const found = findInstance(player, params.sourceInstanceId);
      if (!found || found.where !== 'station' || targetSector == null) return state;
      const a = player.sectors[found.sectorNum];
      const b = player.sectors[targetSector];
      // A colony can never be displaced once bought (this game's own
      // standing rule, already enforced elsewhere - e.g. buyCard's own
      // ship branch) - neither side of an exchange may be one.
      const isColony = (cardId) => Boolean(cardId) && !defFor(cardId).station;
      if (isColony(a.station?.cardId) || isColony(b.station?.cardId)) return state;
      const nextPlayer = {
        ...player,
        sectors: {
          ...player.sectors,
          [found.sectorNum]: { ...a, station: b.station },
          [targetSector]: { ...b, station: a.station },
        },
      };
      return replacePlayer(state, playerIndex, nextPlayer);
    }
    case 'allOpponentsLoseVP': {
      const nextPlayers = state.players.map((p) => (
        p.id === playerId ? p : { ...p, vp: Math.max(0, p.vp - effect.amount) }
      ));
      return { ...state, players: nextPlayers };
    }
    case 'placeChargeAnywhere': {
      const { targetInstanceId, targetSlot } = params;
      const target = findInstance(player, targetInstanceId);
      if (!target) return state;
      const def = defFor(target.instance.cardId);
      const ability = targetSlot === 'station' ? def.station?.ability : def.deployed?.ability;
      if (!ability) return state;
      let nextPlayer = updateInstance(player, targetInstanceId, (inst) => addCharge(inst, targetSlot, ability.squares));
      // Collins (l3-collins) station only: "Place 1 charge anywhere, then
      // move 1 of your charges to another open charge spot." (Its deployed
      // side prints just the place half - verified against the card scan,
      // 2026-08-18 backlog item 3.) The move arrives pre-collected from
      // GameBoard's step picker as params.moveFrom/params.moveTo; it's
      // treated as skippable (there may be no other charge or no open
      // spot), and a param-less activation (the AI) just places - a
      // documented simplification, same class as modifyDieFace's own
      // param defaults.
      if (effect.thenMoveCharge && params.moveFrom && params.moveTo
        && !(params.moveFrom.instanceId === params.moveTo.instanceId && params.moveFrom.slot === params.moveTo.slot)) {
        const from = findInstance(nextPlayer, params.moveFrom.instanceId);
        const to = findInstance(nextPlayer, params.moveTo.instanceId);
        const fromKey = params.moveFrom.slot === 'station' ? 'stationCharges' : 'deployedCharges';
        if (from && to && from.instance[fromKey] > 0) {
          const toDef = defFor(to.instance.cardId);
          const toAbility = params.moveTo.slot === 'station' ? toDef.station?.ability : toDef.deployed?.ability;
          const toKey = params.moveTo.slot === 'station' ? 'stationCharges' : 'deployedCharges';
          if (toAbility && to.instance[toKey] < toAbility.squares) {
            nextPlayer = updateInstance(nextPlayer, params.moveFrom.instanceId, (inst) => ({ ...inst, [fromKey]: inst[fromKey] - 1 }));
            nextPlayer = updateInstance(nextPlayer, params.moveTo.instanceId, (inst) => addCharge(inst, params.moveTo.slot, toAbility.squares));
          }
        }
      }
      return replacePlayer(state, playerIndex, nextPlayer);
    }
    // Genesis's Nikola ("Place 1 charge on all your other cards with an
    // open charge square.") - every one of the player's own cards EXCEPT
    // the one that granted this ability (excluded via `sourceInstanceId`,
    // threaded in by turnActions.js's own useAbility - "OTHER cards" is
    // explicit in the printed text, and this card just spent its own
    // charges down to 0 activating, so without the exclusion it would
    // immediately re-charge itself) gets exactly 1 charge if it has an
    // ability with room left. No player choice involved (unlike Helios/
    // Radiant's "up to 3", still deferred - see
    // docs/expansion-decisions-needed.md). Duplicated (not shared) with
    // turnActions.js's own near-identical chargeAllEligibleCards - that
    // module already imports FROM this one, so the reverse import would
    // be circular (same constraint noted elsewhere this session for
    // heuristicBot.js).
    case 'placeChargeAll': {
      let nextPlayer = player;
      Object.keys(nextPlayer.sectors).forEach((sectorStr) => {
        const sectorNum = Number(sectorStr);
        const sector = nextPlayer.sectors[sectorNum];
        if (sector.station && sector.station.instanceId !== params.sourceInstanceId) {
          const stationAbility = defFor(sector.station.cardId).station?.ability;
          if (stationAbility && sector.station.stationCharges < stationAbility.squares) {
            nextPlayer = updateInstance(nextPlayer, sector.station.instanceId, (inst) => addCharge(inst, 'station', stationAbility.squares));
          }
        }
        sector.deployedStack.forEach((instance) => {
          if (instance.instanceId === params.sourceInstanceId) return;
          const deployedAbility = defFor(instance.cardId).deployed?.ability;
          if (deployedAbility && instance.deployedCharges < deployedAbility.squares) {
            nextPlayer = updateInstance(nextPlayer, instance.instanceId, (inst) => addCharge(inst, 'deployed', deployedAbility.squares));
          }
        });
      });
      return replacePlayer(state, playerIndex, nextPlayer);
    }
    case 'setDice': {
      const dice = state.meta.dice || { values: [null, null], allocations: {} };
      const values = [...dice.values];
      if (effect.dice === 'both' && params.value1 && params.value2) {
        values[0] = params.value1;
        values[1] = params.value2;
      } else if (params.dieIndex != null && params.value) {
        values[params.dieIndex] = params.value;
      }
      return { ...state, meta: { ...state.meta, dice: { ...dice, values, preset: true } } };
    }
    case 'rerollDice': {
      // Actual re-roll (needs RNG) is performed by the caller (turnActions
      // ROLL/REROLL flow); this effect kind is looked up there directly.
      return state;
    }
    case 'youWin': {
      return { ...state, meta: { ...state.meta, instantWinnerId: playerId } };
    }
    // Genesis's Plutus and others (see docs/expansion-decisions-needed.md's
    // own "dual reward+ability" mystery, now resolved for this shape): the
    // charge squares + a printed reward amount was mistaken for a
    // `bonusReward` (which fires on every hit, not on activation) - it's
    // actually a completely ordinary ability effect, just one whose payoff
    // is a flat resource grant on activation rather than an extra buy or a
    // reroll. Fires immediately, no further player input needed.
    case 'grantResource': {
      const nextPlayer = applyResourceReward(player, effect.reward);
      return replacePlayer(state, playerIndex, nextPlayer);
    }
    case 'diceArrowShift': {
      // Only meaningful once a real roll exists for this turn to shift
      // (matches the same "no-op before the dice exist" guard
      // modifyDiceTotal/modifyDieFace both use) - activating before
      // then wastes the charge harmlessly instead of corrupting
      // state.meta.dice into a `{sectorShift: {...}}` object with no
      // `values` at all, which used to crash any LATER code (rerollDice,
      // computeAllocationSectors) that assumes a truthy `dice` always
      // has `.values` - a real pre-existing bug, found via
      // selfPlaySmoke.test.js once a shuffle shift finally reached the
      // exact "activate this pre-roll, then something else touches dice
      // later" sequence for the first time.
      // "You use your re-roll abilities and modify your sector rolls with
      // arrow effects as normal" during a Bonus Roll - a shift activated
      // while this player owns the front pending bonus roll accumulates on
      // THAT entry (sum-only, enforced by resolveBonusRollChoice reading
      // it only in its own 'sum' branch), not the shared turn dice.
      {
        const front = state.meta.pendingBonusRolls?.[0];
        if (front && front.playerId === playerId) {
          const maxShiftB = effect.maxShift ?? effect.shift;
          const chosenB = Math.min(Math.max(params?.shift ?? effect.shift, effect.shift), maxShiftB);
          const priorB = front.sectorShift ? (front.sectorShift.direction === 'left' ? -front.sectorShift.shift : front.sectorShift.shift) : 0;
          const totalB = priorB + ((effect.direction || 'right') === 'left' ? -chosenB : chosenB);
          const nextFront = { ...front, sectorShift: { shift: Math.abs(totalB), direction: totalB < 0 ? 'left' : 'right' } };
          return {
            ...state,
            meta: { ...state.meta, pendingBonusRolls: [nextFront, ...state.meta.pendingBonusRolls.slice(1)] },
          };
        }
      }
      if (!state.meta.dice) return state;
      const { dice } = state.meta;
      // Rulebook p23 ("DICE+ARROW(S)"), verified word for word: a single
      // arrow shifts the reward sector 1 to the RIGHT; "if the ability
      // shows both a -> and a ->-> then... shifted 1 or 2 sectors to the
      // right (your choice)" - the Heinlein Class cards, whose icon an
      // earlier read mistook for a left/right "both directions" choice.
      // effect.maxShift marks the 1-or-2 form; params.shift (GameBoard's
      // picker) picks within [shift..maxShift], clamped, defaulting to the
      // minimum for a param-less activation (the AI).
      const maxShift = effect.maxShift ?? effect.shift;
      const chosen = Math.min(Math.max(params?.shift ?? effect.shift, effect.shift), maxShift);
      // Also p23: "You may combine these types of effects on a single roll
      // to shift your reward sector many spaces along your board" -
      // ACCUMULATES with any shift already active for this player this
      // roll (this used to overwrite, silently eating the earlier
      // activation's charge).
      const shifts = { ...(dice.sectorShift || {}) };
      const prior = shifts[playerId] ? (shifts[playerId].direction === 'left' ? -shifts[playerId].shift : shifts[playerId].shift) : 0;
      const total = prior + ((effect.direction || 'right') === 'left' ? -chosen : chosen);
      shifts[playerId] = { shift: Math.abs(total), direction: total < 0 ? 'left' : 'right' };
      return { ...state, meta: { ...state.meta, dice: { ...dice, sectorShift: shifts } } };
    }
    // Several Genesis ships (Rogue Star/Iron Dragon/Black Diamond): "This
    // turn keep all credits you do not spend." Overrides the base game's
    // own rule that a normal (non-extra) buy always spends the WHOLE
    // credit cube to 0 regardless of cost - buyCard's own colony/ship
    // branches check this flag and subtract just the cost instead, same
    // as a non-free extra buy already does. Tracked per-player (not just
    // the active player) since it's a green-timing ability - cleared for
    // whoever's own turn ends in endTurn(), matching "this turn" exactly.
    case 'keepUnspentCredits': {
      const ids = state.meta.keepsUnspentCreditsPlayerIds || [];
      if (ids.includes(playerId)) return state;
      return { ...state, meta: { ...state.meta, keepsUnspentCreditsPlayerIds: [...ids, playerId] } };
    }
    default:
      return state;
  }
}

function replacePlayer(state, index, player) {
  const players = state.players.slice();
  players[index] = player;
  return { ...state, players };
}

// Genesis's Reese/Nunn/Yaple/Veger ("Until the end of your next turn,
// when you would gain credits/income/vp, [double it / convert it to vp]
// instead.") - every plain resource grant in the whole engine funnels
// through applyResourceToPlayer (grantReward's own default case, plus a
// handful of direct colony/ability grants), so this single choke point
// is where all four apply, rather than threading a modifier lookup
// through every individual call site. `excludeColonySource` is Yaple's
// own printed carve-out ("except vp from colonies") - `reward.source:
// 'colony'` is set by the two colony vp grants in buyCard (the top
// reward and excessCreditsToVp) so this can tell those apart from a
// normal dice-hit vp reward.
function applyTemporaryResourceModifiers(state, playerId, reward) {
  if (!reward || (reward.kind !== 'credits' && reward.kind !== 'income' && reward.kind !== 'vp')) return reward;
  const modifiers = state.meta.resourceModifiers?.[playerId] || [];
  return modifiers.reduce((r, mod) => {
    if (mod.resourceKind !== r.kind) return r;
    if (mod.excludeColonySource && r.source === 'colony') return r;
    if (mod.mode === 'double') return { ...r, amount: r.amount * 2 };
    if (mod.mode === 'convert') return { kind: mod.convertTo, amount: r.amount };
    return r;
  }, reward);
}

export function applyResourceToPlayer(state, playerId, reward) {
  const index = state.players.findIndex((p) => p.id === playerId);
  const adjusted = applyTemporaryResourceModifiers(state, playerId, reward);
  return replacePlayer(state, index, applyResourceReward(state.players[index], adjusted));
}

export { findInstance, updateInstance };
